// ─── estimateCurrentQuantity ──────────────────────────────────────────────────

export function estimateCurrentQuantity(
  quantityBought:     number,
  quantityUpdatedAt:  Date,
  avgRestockCycleDays: number | null,
  estimatedDailyUse:  number | null,
): number {
  if (!quantityUpdatedAt) return 0

  const daysSinceUpdate =
    (Date.now() - quantityUpdatedAt.getTime()) / (1000 * 60 * 60 * 24)

  if (estimatedDailyUse) {
    const estimated = quantityBought - estimatedDailyUse * daysSinceUpdate
    return Math.max(0, estimated)
  }

  if (avgRestockCycleDays) {
    const dailyUse  = quantityBought / avgRestockCycleDays
    const estimated = quantityBought - dailyUse * daysSinceUpdate
    return Math.max(0, estimated)
  }

  return quantityBought
}

// ─── getQuantityStatus ────────────────────────────────────────────────────────

export function getQuantityStatus(
  estimatedQuantity: number,
  originalQuantity:  number,
): {
  percentage: number
  label:      string
  color:      string
  isLow:      boolean
  isEmpty:    boolean
} {
  if (originalQuantity <= 0) {
    return { percentage: 0, label: 'Unknown', color: '#9E9E9E', isLow: false, isEmpty: false }
  }

  const percentage = Math.min(100, (estimatedQuantity / originalQuantity) * 100)

  if (percentage <= 0) {
    return { percentage: 0, label: 'Empty', color: '#C25B4E', isLow: true, isEmpty: true }
  }

  if (percentage <= 20) {
    return {
      percentage,
      label:   `~${Math.round(estimatedQuantity)} left`,
      color:   '#C25B4E',
      isLow:   true,
      isEmpty: false,
    }
  }

  if (percentage <= 40) {
    return {
      percentage,
      label:   `~${Math.round(estimatedQuantity)} left`,
      color:   '#D4863A',
      isLow:   true,
      isEmpty: false,
    }
  }

  return {
    percentage,
    label:   `${Math.round(estimatedQuantity)} left`,
    color:   '#6B9E78',
    isLow:   false,
    isEmpty: false,
  }
}

// ─── calculateSaleDetails ─────────────────────────────────────────────────────

export function calculateSaleDetails(
  totalPaid:     number,
  quantity:      number,
  regularPrice:  number | null,
): {
  pricePerUnit:      number
  totalSaving:       number | null
  savingPerUnit:     number | null
  savingPercentage:  number | null
} {
  const pricePerUnit = totalPaid / quantity

  if (!regularPrice || regularPrice <= 0) {
    return { pricePerUnit, totalSaving: null, savingPerUnit: null, savingPercentage: null }
  }

  const regularTotal      = regularPrice * quantity
  const totalSaving       = regularTotal - totalPaid
  const savingPerUnit     = regularPrice - pricePerUnit
  const savingPercentage  = (savingPerUnit / regularPrice) * 100

  return { pricePerUnit, totalSaving, savingPerUnit, savingPercentage }
}
